import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { api } from "@/lib/api";
import Prism from "@/components/Prism";
import { ArrowRight, Languages, Loader2, Mail, Lock, Eye, EyeOff, X, AlertCircle, CheckCircle2 } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const useMedia = (query) => {
  const [matches, setMatches] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const onChange = (e) => setMatches(e.matches);
    mq.addEventListener("change", onChange);
    setMatches(mq.matches);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.6 6.3 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.8 15.7 19 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.6 6.3 29.1 4.5 24 4.5 16.5 4.5 10 8.7 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 43.5c5 0 9.6-1.7 13.1-4.6l-6-5.1c-1.9 1.3-4.4 2.2-7.1 2.2-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C10 39.3 16.5 43.5 24 43.5z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6 5.1c-.4.4 6.6-4.8 6.6-14.6 0-1.2-.1-2.3-.4-3.5z" />
  </svg>
);

const GlassDialog = ({ open, onOpenChange, title, children, testid }) => (
  <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className="fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        style={{ background: "rgba(36,20,10,.42)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)" }}
      />
      <DialogPrimitive.Content
        data-testid={testid}
        className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-32px)] max-w-[384px] -translate-x-1/2 -translate-y-1/2 p-6 sm:p-7 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        style={{
          borderRadius: 24,
          background: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(18px) saturate(140%)",
          WebkitBackdropFilter: "blur(18px) saturate(140%)",
          border: "1px solid rgba(255,255,255,0.7)",
          boxShadow: "0 24px 60px -24px rgba(36,20,10,0.35)",
        }}
      >
        <DialogPrimitive.Title className="text-xl font-semibold tracking-tight" style={{ color: "#221a15", fontFamily: "'Outfit', sans-serif" }}>
          {title}
        </DialogPrimitive.Title>
        {children}
        <DialogPrimitive.Close
          data-testid="auth-dialog-close"
          aria-label="Close"
          className="absolute right-4 top-4 rounded-full p-1.5 text-[#857a72] transition-colors hover:bg-black/5 hover:text-[#221a15] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c2410c]"
        >
          <X className="h-4 w-4" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>
);

const Login = () => {
  const { user, setUser } = useAuth();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  const [dialog, setDialog] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [prismFailed, setPrismFailed] = useState(false);

  const isDesktop = useMedia("(min-width: 768px)");
  const reduced = useMedia("(prefers-reduced-motion: reduce)");

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    const notice = location.state?.notice;
    if (notice) {
      setAlert({ type: location.state?.noticeType === "error" ? "error" : "success", text: notice });
      setDialog("signin");
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setAlert({ type: "error", text: t("email_password_required") }); return; }
    setLoading(true);
    setAlert(null);
    try {
      const res = await api.post("/auth/login", { email, password });
      setUser(res.data.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setAlert({ type: "error", text: formatApiErrorDetail(err?.response?.data?.detail) || err.message });
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (next) => { setAlert(null); setDialog(next); };

  const rise = useMemo(() => (i) => (reduced
    ? { initial: false, animate: { opacity: 1, y: 0 } }
    : {
      initial: { opacity: 0, y: 8 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.5, delay: i * 0.055, ease: EASE },
    }), [reduced]);

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden"
      style={{ background: "#fcfbfa", fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      data-testid="auth-landing-view"
    >
      {/* background layers */}
      {(!isDesktop || prismFailed) && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(60% 50% at 50% 100%, rgba(251,146,60,.30), transparent 70%)," +
              "radial-gradient(50% 40% at 15% 20%, rgba(253,230,138,.30), transparent 70%)," +
              "radial-gradient(50% 40% at 85% 25%, rgba(254,215,170,.40), transparent 70%)",
          }}
        />
      )}
      {isDesktop && (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0" data-testid="prism-background">
          <Prism
            animationType="rotate"
            timeScale={reduced ? 0 : 0.5}
            height={3.5}
            baseWidth={5.5}
            scale={3.6}
            hueShift={0}
            colorFrequency={1}
            noise={reduced ? 0 : 0.5}
            glow={1}
            suspendWhenOffscreen
            onUnsupported={() => setPrismFailed(true)}
          />
        </div>
      )}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(42% 34% at 50% 46%, rgba(255,255,255,.78), rgba(255,255,255,.35) 55%, transparent 80%)",
        }}
      />

      {/* language toggle */}
      <div className="absolute right-5 top-5 z-10">
        <button
          onClick={toggle}
          data-testid="language-toggle"
          className="flex items-center gap-2 rounded-full px-3 py-2 text-sm transition-shadow hover:shadow-md"
          style={{
            background: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid #eee8e3",
            color: "#574d46",
          }}
        >
          <Languages className="h-4 w-4" style={{ color: "#857a72" }} />
          <span className={`font-semibold ${lang === "id" ? "" : "opacity-50"}`} style={{ color: lang === "id" ? "#c2410c" : "#574d46" }}>ID</span>
          <span className={`font-semibold ${lang === "en" ? "" : "opacity-50"}`} style={{ color: lang === "en" ? "#c2410c" : "#574d46" }}>EN</span>
        </button>
      </div>

      {/* centered stack */}
      <div className="relative z-10 flex min-h-screen w-full items-center justify-center px-6">
        <div className="w-full max-w-3xl text-center">
          <motion.div {...rise(0)} className="flex justify-center">
            <div
              className="inline-flex items-center gap-2 rounded-full py-1.5 pl-1.5 pr-4"
              style={{
                background: "rgba(255,255,255,0.75)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                border: "1px solid #eee8e3",
                boxShadow: "0 1px 2px rgba(36,20,10,.06), 0 4px 12px -6px rgba(36,20,10,.12)",
              }}
            >
              <span
                className="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase text-white"
                style={{ background: "#221a15", letterSpacing: ".08em" }}
              >
                INTERNAL
              </span>
              <span className="text-xs" style={{ color: "#574d46" }}>{t("auth_badge")}</span>
            </div>
          </motion.div>

          <motion.h1
            {...rise(1)}
            className="mt-6 text-[36px] font-semibold sm:text-[48px] md:text-[60px]"
            style={{ color: "#221a15", lineHeight: 1.05, letterSpacing: "-0.025em", fontFamily: "'Outfit', sans-serif" }}
          >
            CRM Internal
          </motion.h1>

          <motion.p {...rise(2)} className="mt-5 text-base md:text-lg" style={{ color: "#574d46" }}>
            for Jasa Ferrie Pratama
          </motion.p>

          <motion.div {...rise(3)} className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => openDialog("signin")}
              data-testid="open-signin-dialog"
              className="group inline-flex h-12 min-w-[144px] items-center justify-center gap-2 rounded-xl px-6 font-semibold text-white transition-all duration-200"
              style={{ background: "#c2410c", boxShadow: "0 10px 20px -12px rgba(36,20,10,.45)" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#9a3412";
                e.currentTarget.style.boxShadow = "0 6px 20px -6px rgba(234,88,12,.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#c2410c";
                e.currentTarget.style.boxShadow = "0 10px 20px -12px rgba(36,20,10,.45)";
              }}
            >
              {t("sign_in")}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={() => openDialog("register")}
              data-testid="open-register-dialog"
              className="inline-flex h-12 min-w-[144px] items-center justify-center rounded-xl px-6 font-semibold transition-colors duration-200 hover:bg-white"
              style={{
                background: "rgba(255,255,255,0.70)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                border: "1px solid rgba(255,255,255,0.6)",
                color: "#221a15",
              }}
            >
              {t("register")}
            </button>
          </motion.div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-6 z-10 px-6 text-center text-xs" style={{ color: "#685e57" }}>
        {t("auth_footer")}
      </div>

      {/* sign in dialog */}
      <GlassDialog
        open={dialog === "signin"}
        onOpenChange={(o) => setDialog(o ? "signin" : null)}
        title={t("sign_in")}
        testid="signin-dialog"
      >
        <p className="mt-2 text-xs leading-relaxed" style={{ color: "#574d46" }}>{t("signin_intro")}</p>

        {alert && (
          <div
            data-testid="auth-alert"
            className="mt-4 flex items-start gap-2 rounded-xl px-3 py-2 text-xs"
            style={alert.type === "error"
              ? { background: "rgba(254,226,226,.9)", border: "1px solid #fecaca", color: "#9f1239" }
              : { background: "rgba(220,252,231,.9)", border: "1px solid #bbf7d0", color: "#166534" }}
          >
            {alert.type === "error" ? <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
            <span>{alert.text}</span>
          </div>
        )}

        <button
          onClick={handleGoogle}
          data-testid="google-login-button"
          className="mt-4 flex h-11 w-full items-center justify-center gap-3 rounded-xl bg-white font-semibold transition-shadow hover:shadow-md"
          style={{ border: "1px solid #eee8e3", color: "#221a15" }}
        >
          <GoogleIcon /> {t("sign_in_google")}
        </button>

        <div className="my-4 flex items-center gap-3">
          <span className="h-px flex-1" style={{ background: "#eee8e3" }} />
          <span className="text-[11px] uppercase tracking-wider" style={{ color: "#857a72" }}>{t("or_with_email")}</span>
          <span className="h-px flex-1" style={{ background: "#eee8e3" }} />
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="relative">
            <Mail className="absolute left-3 top-3.5 h-4 w-4" style={{ color: "#857a72" }} />
            <input
              data-testid="auth-email-input"
              type="email"
              autoComplete="email"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 w-full rounded-xl bg-white/80 pl-9 pr-3 text-sm outline-none transition-shadow focus:ring-2"
              style={{ border: "1px solid #eee8e3", color: "#221a15" }}
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-3.5 h-4 w-4" style={{ color: "#857a72" }} />
            <input
              data-testid="auth-password-input"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 w-full rounded-xl bg-white/80 pl-9 pr-10 text-sm outline-none transition-shadow focus:ring-2"
              style={{ border: "1px solid #eee8e3", color: "#221a15" }}
            />
            <button
              type="button"
              data-testid="toggle-password-visibility"
              aria-label="toggle password"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-2 top-2.5 rounded-lg p-1.5 hover:bg-black/5"
              style={{ color: "#857a72" }}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="email-login-button"
            className="flex h-11 w-full items-center justify-center gap-2 rounded-xl font-semibold text-white transition-colors disabled:opacity-70"
            style={{ background: "#c2410c", boxShadow: "0 10px 20px -12px rgba(36,20,10,.45)" }}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? t("checking") : t("sign_in")}
          </button>
        </form>

        <p className="mt-4 text-center text-xs" style={{ color: "#685e57" }}>
          {t("no_access_yet")}{" "}
          <button onClick={() => openDialog("register")} data-testid="switch-to-register" className="font-semibold underline" style={{ color: "#c2410c" }}>
            {t("register")}
          </button>
        </p>
      </GlassDialog>

      {/* register dialog */}
      <GlassDialog
        open={dialog === "register"}
        onOpenChange={(o) => setDialog(o ? "register" : null)}
        title={t("create_account_title")}
        testid="register-dialog"
      >
        <p className="mt-2 text-xs leading-relaxed" style={{ color: "#574d46" }}>{t("register_intro")}</p>

        <button
          onClick={handleGoogle}
          data-testid="google-register-button"
          className="mt-4 flex h-11 w-full items-center justify-center gap-3 rounded-xl font-semibold text-white transition-colors"
          style={{ background: "#c2410c", boxShadow: "0 10px 20px -12px rgba(36,20,10,.45)" }}
        >
          <GoogleIcon /> {t("register_google")}
        </button>

        <ol className="mt-5 space-y-3">
          {[t("register_step_1"), t("register_step_2"), t("register_step_3")].map((step, i) => (
            <li key={step} className="flex items-start gap-3 text-xs" style={{ color: "#574d46" }}>
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
                style={{ background: "#ffedd5", color: "#c2410c" }}
              >
                {i + 1}
              </span>
              <span className="leading-relaxed">{step}</span>
            </li>
          ))}
        </ol>

        <p className="mt-5 text-center text-xs" style={{ color: "#685e57" }}>
          {t("have_access")}{" "}
          <button onClick={() => openDialog("signin")} data-testid="switch-to-signin" className="font-semibold underline" style={{ color: "#c2410c" }}>
            {t("sign_in")}
          </button>
        </p>
      </GlassDialog>
    </div>
  );
};

export default Login;
